import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Dropdown,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  ExportOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SolutionOutlined,
  TeamOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import OpsNotificationBell from "../../components/OpsNotificationBell";
import {
  activateOpsPatient,
  activateOpsProvider,
  approveOpsProvider,
  assignDoctorToHospital,
  bulkRegisterProviders,
  closeOpsTicket,
  createHospital,
  createOpsTicket,
  deactivateOpsPatient,
  deactivateOpsProvider,
  downloadBulkTemplate,
  enrollOpsPatient,
  escalateTicket,
  exportDatabase,
  getHospitalDoctors,
  getMyInvoice,
  getMyInvoices,
  getMyOrganisation,
  getOpsStats,
  listHospitals,
  listInboxTickets,
  listOpsPatients,
  listOpsProviders,
  listOpsTickets,
  registerOpsProvider,
  removeDoctorFromHospital,
  resetPatientPassword,
  resetProviderPassword,
  respondToTicket,
  unverifyPatientId,
  verifyPatientId,
} from "../../services/operationsService";
import {
  approveSignupRequest,
  getSignupRequests,
  rejectSignupRequest,
} from "../../services/adminService";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const toArray = (data) =>
  Array.isArray(data) ? data : (data?.results ?? []);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const fullName = (r) =>
  [r?.first_name, r?.last_name].filter(Boolean).join(" ") || r?.email || "—";

function idStatusTag(record) {
  if (record.government_id_verified)
    return <Tag color="success">Verified</Tag>;
  if (record.government_id_photo_url)
    return <Tag color="warning">Pending</Tag>;
  return <Tag color="default">No ID</Tag>;
}

const CATEGORY_LABEL = {
  BILLING:            "Billing Issue",
  TECHNICAL:          "Technical Problem",
  ACCOUNT_MANAGEMENT: "Account Management",
  USER_ISSUE:         "User Issue",
  CONTRACT:           "Contract Question",
  OTHER:              "Other",
};

const PRIORITY_LABEL = { LOW: "Low", MEDIUM: "Normal", HIGH: "High", CRITICAL: "Urgent" };
const PRIORITY_COLOR = { LOW: "default", MEDIUM: "blue", HIGH: "orange", CRITICAL: "red" };
const TICKET_STATUS_COLOR = {
  OPEN: "orange", IN_PROGRESS: "blue", RESOLVED: "green",
  CLOSED: "default", ESCALATED: "red",
};

// Dynamic MENU_ITEMS is built in the component so we can embed the badge count
function buildMenuItems(openCount, pendingSignups) {
  return [
    { key: "overview",         icon: <DashboardOutlined />,  label: "Overview" },
    { key: "my-org",           icon: <BankOutlined />,       label: "My Organisation" },
    {
      key: "signup-requests",
      icon: <SolutionOutlined />,
      label: pendingSignups > 0
        ? <Badge count={pendingSignups} size="small" offset={[6, 0]}>Signup Requests</Badge>
        : "Signup Requests",
    },
    { key: "hospitals",        icon: <BankOutlined />,        label: "Hospital Management" },
    { key: "patients",         icon: <TeamOutlined />,        label: "Patient Management" },
    { key: "doctors",          icon: <MedicineBoxOutlined />, label: "Doctor Management" },
    { key: "pharmacists",      icon: <MedicineBoxOutlined />, label: "Pharmacy Management" },
    { key: "bulk-registration",icon: <UploadOutlined />,      label: "Bulk Registration" },
    {
      key: "support",
      icon: <CustomerServiceOutlined />,
      label: openCount > 0
        ? <Badge count={openCount} size="small" offset={[6, 0]}>Support</Badge>
        : "Support",
    },
    { key: "profile",          icon: <UserOutlined />,        label: "My Profile" },
  ];
}

export default function OperationsManagerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("overview");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);

  // signup requests
  const [signupRows,        setSignupRows]        = useState([]);
  const [signupLoading,     setSignupLoading]     = useState(false);
  const [signupLoaded,      setSignupLoaded]      = useState(false);
  const [signupRejectModal, setSignupRejectModal] = useState(null);
  const [rejectReason,      setRejectReason]      = useState("");
  const [approveModal,      setApproveModal]      = useState(null);

  // data
  const [stats,       setStats]       = useState({});
  const [myOrg,       setMyOrg]       = useState(null);
  const [patients,    setPatients]    = useState([]);
  const [doctors,     setDoctors]     = useState([]);
  const [pharmacists, setPharmacists] = useState([]);

  // patient list controls
  const [patientSearch, setPatientSearch] = useState("");
  const [patientFilter, setPatientFilter] = useState("all");

  // provider list controls
  const [doctorFilter,    setDoctorFilter]    = useState("all");
  const [pharmacistFilter,setPharmacistFilter]= useState("all");

  // modals / drawer
  const [enrollOpen,        setEnrollOpen]        = useState(false);
  const [enrollForm]                               = Form.useForm();
  const [doctorRegOpen,     setDoctorRegOpen]     = useState(false);
  const [doctorRegForm]                            = Form.useForm();
  const [pharmacistRegOpen, setPharmacistRegOpen] = useState(false);
  const [pharmacistRegForm]                        = Form.useForm();
  const [providerRegResult, setProviderRegResult] = useState(null);
  const [detailOpen,        setDetailOpen]        = useState(false);
  const [detailRecord,      setDetailRecord]      = useState(null);
  const [resetPassResult,   setResetPassResult]   = useState(null);
  const [submitting,        setSubmitting]        = useState(false);

  // support tickets — my own (submitted to admin)
  const [tickets,         setTickets]         = useState([]);
  const [ticketsLoaded,   setTicketsLoaded]   = useState(false);
  const [ticketFilter,    setTicketFilter]    = useState("ALL");
  const [ticketCreateOpen,setTicketCreateOpen]= useState(false);
  const [ticketDetail,    setTicketDetail]    = useState(null);
  const [ticketForm]                           = Form.useForm();

  // support tickets — user inbox (submitted TO this manager by end users)
  const [inboxTickets,    setInboxTickets]    = useState([]);
  const [inboxLoaded,     setInboxLoaded]     = useState(false);
  const [inboxFilter,     setInboxFilter]     = useState("ALL");
  const [supportTab,      setSupportTab]      = useState("my-tickets");

  // invoices
  const [myInvoices,         setMyInvoices]         = useState([]);
  const [invoicesLoaded,     setInvoicesLoaded]     = useState(false);
  const [invoicesLoading,    setInvoicesLoading]    = useState(false);
  const [invoiceDetailOpen,  setInvoiceDetailOpen]  = useState(false);
  const [invoiceDetailData,  setInvoiceDetailData]  = useState(null);
  const [invoiceDetailLoading, setInvoiceDetailLoading] = useState(false);

  // respond / escalate state
  const [respondOpen,     setRespondOpen]     = useState(false);
  const [respondTarget,   setRespondTarget]   = useState(null);
  const [respondForm]                          = Form.useForm();
  const [escalateOpen,    setEscalateOpen]    = useState(false);
  const [escalateTarget,  setEscalateTarget]  = useState(null);
  const [escalateForm]                         = Form.useForm();

  // hospitals
  const [hospitals,        setHospitals]        = useState([]);
  const [hospitalsLoaded,  setHospitalsLoaded]  = useState(false);
  const [hospitalsLoading, setHospitalsLoading] = useState(false);
  const [hospitalModal,    setHospitalModal]    = useState(false);
  const [hospitalForm]                           = Form.useForm();
  const [hospitalDetail,   setHospitalDetail]   = useState(null);
  const [hospitalDocs,     setHospitalDocs]     = useState([]);
  const [assignDocModal,   setAssignDocModal]   = useState(false);
  const [assignDocTarget,  setAssignDocTarget]  = useState(null);
  const [assignDocForm]                          = Form.useForm();

  // bulk registration
  const [bulkFile,    setBulkFile]    = useState(null);
  const [bulkResult,  setBulkResult]  = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // ── initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [statsRes, orgRes, patientsRes, doctorsRes, pharmsRes] = await Promise.all([
          getOpsStats(),
          getMyOrganisation().catch(() => ({ data: null })),
          listOpsPatients(),
          listOpsProviders({ role: "DOCTOR" }),
          listOpsProviders({ role: "PHARMACIST" }),
        ]);
        if (cancelled) return;
        setStats(statsRes.data ?? {});
        setMyOrg(orgRes.data ?? null);
        setPatients(toArray(patientsRes.data).map(r => ({ ...r, key: r.id })));
        setDoctors(toArray(doctorsRes.data).map(r => ({ ...r, key: r.id })));
        setPharmacists(toArray(pharmsRes.data).map(r => ({ ...r, key: r.id })));
      } catch {
        if (!cancelled) setError("Failed to load dashboard data. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── patient action handlers ───────────────────────────────────────────────
  const handleVerifyId = async (record) => {
    try {
      await verifyPatientId(record.id);
      setPatients(prev => prev.map(p =>
        p.id === record.id ? { ...p, government_id_verified: true } : p
      ));
      if (detailRecord?.id === record.id)
        setDetailRecord(r => ({ ...r, government_id_verified: true }));
      message.success("Government ID verified.");
    } catch {
      message.error("Failed to verify ID.");
    }
  };

  const handleUnverifyId = async (record) => {
    try {
      await unverifyPatientId(record.id);
      setPatients(prev => prev.map(p =>
        p.id === record.id ? { ...p, government_id_verified: false } : p
      ));
      if (detailRecord?.id === record.id)
        setDetailRecord(r => ({ ...r, government_id_verified: false }));
      message.success("ID verification removed.");
    } catch {
      message.error("Failed to unverify ID.");
    }
  };

  const handleActivatePatient = async (record) => {
    try {
      await activateOpsPatient(record.id);
      setPatients(prev => prev.map(p =>
        p.id === record.id ? { ...p, is_active: true } : p
      ));
      if (detailRecord?.id === record.id)
        setDetailRecord(r => ({ ...r, is_active: true }));
      message.success("Account activated.");
    } catch {
      message.error("Failed to activate account.");
    }
  };

  const handleDeactivatePatient = async (record) => {
    try {
      await deactivateOpsPatient(record.id);
      setPatients(prev => prev.map(p =>
        p.id === record.id ? { ...p, is_active: false } : p
      ));
      if (detailRecord?.id === record.id)
        setDetailRecord(r => ({ ...r, is_active: false }));
      message.success("Account deactivated.");
    } catch {
      message.error("Failed to deactivate account.");
    }
  };

  const handleResetPassword = (record, type) => {
    Modal.confirm({
      title: `Reset password for ${fullName(record)}?`,
      content: "A new temporary password will be generated. Communicate it securely.",
      okText: "Reset Password",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = type === "patient"
            ? await resetPatientPassword(record.id)
            : await resetProviderPassword(record.id);
          setResetPassResult({
            name: fullName(record),
            temp_password: res.data.temp_password,
          });
        } catch {
          message.error("Failed to reset password.");
        }
      },
    });
  };

  const handleEnrollPatient = async (values) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([k, v]) => {
        if (v == null) return;
        if (k === "government_id_photo" && v?.fileList?.[0]?.originFileObj) {
          formData.append(k, v.fileList[0].originFileObj);
        } else {
          formData.append(k, v);
        }
      });
      const res = await enrollOpsPatient(formData);
      message.success(`Patient enrolled. PIN: ${res.data.pin}`);
      if (res.data.organisation) message.info(`Assigned to: ${res.data.organisation}`);
      setEnrollOpen(false);
      enrollForm.resetFields();
      setRetryCount(c => c + 1);
    } catch (err) {
      const d = err?.response?.data;
      message.error(
        d?.email?.[0] ?? d?.government_id_number?.[0] ?? d?.detail ?? "Enrollment failed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterProvider = async (values, role, form, setOpen) => {
    setSubmitting(true);
    try {
      const res = await registerOpsProvider({ ...values, role });
      const name = [values.first_name, values.last_name].filter(Boolean).join(" ");
      const prefix = role === "DOCTOR" ? "Dr." : "";
      form.resetFields();
      setOpen(false);
      setProviderRegResult({
        name: `${prefix} ${name}`.trim(),
        email: res.data.email,
        temp_password: res.data.temp_password,
        role,
      });
      setStats(s => ({
        ...s,
        total_doctors:      role === "DOCTOR"     ? (s.total_doctors ?? 0) + 1     : (s.total_doctors ?? 0),
        total_pharmacists:  role === "PHARMACIST" ? (s.total_pharmacists ?? 0) + 1 : (s.total_pharmacists ?? 0),
      }));
      if (role === "DOCTOR")
        setDoctors(prev => [{ ...res.data, key: res.data.id }, ...prev]);
      else
        setPharmacists(prev => [{ ...res.data, key: res.data.id }, ...prev]);
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.email?.[0] ?? d?.detail ?? `Failed to register ${role.toLowerCase()}.`);
    } finally {
      setSubmitting(false);
    }
  };

  // ── provider action handlers ──────────────────────────────────────────────
  const handleActivateProvider = async (record, setList) => {
    try {
      await activateOpsProvider(record.id);
      setList(prev => prev.map(p =>
        p.id === record.id ? { ...p, is_active: true } : p
      ));
      message.success("Provider account activated.");
    } catch {
      message.error("Failed to activate provider.");
    }
  };

  const handleDeactivateProvider = async (record, setList) => {
    try {
      await deactivateOpsProvider(record.id);
      setList(prev => prev.map(p =>
        p.id === record.id ? { ...p, is_active: false } : p
      ));
      message.success("Provider account deactivated.");
    } catch {
      message.error("Failed to deactivate provider.");
    }
  };

  const handleApproveProvider = async (record, setList) => {
    try {
      await approveOpsProvider(record.id);
      setList(prev => prev.map(p =>
        p.id === record.id
          ? { ...p, is_active: true, is_verified: true }
          : p
      ));
      message.success("Provider approved successfully.");
      setStats(s => ({
        ...s,
        pending_provider_approvals: Math.max(0, (s.pending_provider_approvals ?? 0) - 1),
      }));
    } catch {
      message.error("Failed to approve provider.");
    }
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [
    { key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: handleLogout },
  ];

  const openInvoiceDetail = async (inv) => {
    setInvoiceDetailOpen(true);
    setInvoiceDetailLoading(true);
    try {
      const res = await getMyInvoice(inv.id);
      setInvoiceDetailData(res.data);
      setMyInvoices(prev => prev.map(i => i.id === inv.id ? res.data : i));
    } catch {
      message.error("Failed to load invoice.");
      setInvoiceDetailOpen(false);
    } finally {
      setInvoiceDetailLoading(false);
    }
  };

  const exportInvoiceCSV = (inv) => {
    if (!inv) return;
    const rows = [
      ["Invoice Number", inv.invoice_number],
      ["Organisation",   inv.organisation_name],
      ["Period",         inv.period_label],
      ["Status",         inv.status],
      ["Due Date",       inv.due_date ?? ""],
      [""],
      ["Service",                    "Count", "Amount"],
      ["Patient Registration",       inv.total_registrations,      parseFloat(inv.registration_amount).toFixed(2)],
      ["Prescription Issued",        inv.total_prescriptions,      parseFloat(inv.prescription_amount).toFixed(2)],
      ["Prescription Dispensed",     inv.total_dispenses,          parseFloat(inv.dispense_amount).toFixed(2)],
      ["Eye Examination",            inv.total_eye_exams,          parseFloat(inv.eye_exam_amount).toFixed(2)],
      ["Lens Prescription Issued",   inv.total_lens_prescriptions, parseFloat(inv.lens_prescription_amount).toFixed(2)],
      [""],
      ["Total", "", parseFloat(inv.total_amount).toFixed(2)],
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `${inv.invoice_number}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── support ticket handlers ───────────────────────────────────────────────
  useEffect(() => {
    if (selectedMenu !== "support" || ticketsLoaded) return;
    listOpsTickets()
      .then(res => {
        setTickets(toArray(res.data).map((t, i) => ({ ...t, key: t.id ?? i })));
        setTicketsLoaded(true);
      })
      .catch(() => message.error("Failed to load support tickets."));
  }, [selectedMenu, ticketsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMenu !== "support" || inboxLoaded) return;
    listInboxTickets()
      .then(res => {
        setInboxTickets(toArray(res.data).map((t, i) => ({ ...t, key: t.id ?? i })));
        setInboxLoaded(true);
      })
      .catch(() => message.error("Failed to load user inbox."));
  }, [selectedMenu, inboxLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMenu !== "my-org" || invoicesLoaded) return;
    setInvoicesLoading(true);
    getMyInvoices()
      .then(res => {
        setMyInvoices(Array.isArray(res.data) ? res.data : (res.data?.results ?? []));
        setInvoicesLoaded(true);
      })
      .catch(() => {})
      .finally(() => setInvoicesLoading(false));
  }, [selectedMenu, invoicesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMenu !== "signup-requests" || signupLoaded) return;
    setSignupLoading(true);
    getSignupRequests()
      .then(res => {
        setSignupRows((res.data?.results ?? res.data ?? []).map((r, i) => ({ ...r, key: r.id ?? i })));
        setSignupLoaded(true);
      })
      .catch(() => message.error("Failed to load signup requests."))
      .finally(() => setSignupLoading(false));
  }, [selectedMenu, signupLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMenu !== "hospitals" || hospitalsLoaded) return;
    setHospitalsLoading(true);
    listHospitals()
      .then(res => {
        setHospitals(toArray(res.data).map(h => ({ ...h, key: h.id })));
        setHospitalsLoaded(true);
      })
      .catch(() => message.error("Failed to load hospitals."))
      .finally(() => setHospitalsLoading(false));
  }, [selectedMenu, hospitalsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateTicket = async (values) => {
    setSubmitting(true);
    try {
      const res = await createOpsTicket(values);
      const newTicket = { ...res.data, key: res.data.id };
      setTickets(prev => [newTicket, ...prev]);
      message.success("Support ticket submitted.");
      setTicketCreateOpen(false);
      ticketForm.resetFields();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.subject?.[0] ?? d?.description?.[0] ?? d?.detail ?? "Failed to submit ticket.");
    } finally { setSubmitting(false); }
  };

  const handleCloseTicket = async (ticket) => {
    try {
      const res = await closeOpsTicket(ticket.id);
      setTickets(prev => prev.map(t => t.id === ticket.id ? { ...res.data, key: ticket.id } : t));
      setTicketDetail(prev => prev?.id === ticket.id ? { ...res.data, key: ticket.id } : prev);
      message.success("Ticket closed.");
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to close ticket.");
    }
  };

  const handleRespondToTicket = async (values) => {
    setSubmitting(true);
    try {
      const res = await respondToTicket(respondTarget.id, values);
      const updated = { ...res.data, key: respondTarget.id };
      setInboxTickets(prev => prev.map(t => t.id === respondTarget.id ? updated : t));
      message.success("Response sent.");
      setRespondOpen(false);
      respondForm.resetFields();
      setRespondTarget(null);
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to send response.");
    } finally { setSubmitting(false); }
  };

  const handleEscalateTicket = async (values) => {
    setSubmitting(true);
    try {
      const res = await escalateTicket(escalateTarget.id, { note: values.note });
      const updated = { ...res.data, key: escalateTarget.id };
      setInboxTickets(prev => prev.map(t => t.id === escalateTarget.id ? updated : t));
      message.success("Ticket escalated to MCHUSA successfully. You will be contacted shortly.");
      setEscalateOpen(false);
      escalateForm.resetFields();
      setEscalateTarget(null);
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to escalate ticket.");
    } finally { setSubmitting(false); }
  };

  // ── patient dropdown actions ──────────────────────────────────────────────
  const patientDropdownItems = (record) => {
    const items = [];
    if (record.government_id_photo_url && !record.government_id_verified) {
      items.push({
        key: "verify",
        label: <span><CheckCircleOutlined style={{ marginRight: 6 }} />Verify ID</span>,
        onClick: () => handleVerifyId(record),
      });
    }
    if (record.government_id_verified) {
      items.push({
        key: "unverify",
        label: (
          <Popconfirm
            title="Remove ID verification?"
            description="The ID status will return to Pending."
            onConfirm={() => handleUnverifyId(record)}
            okText="Unverify" okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onClick={e => e.stopPropagation()}>
            <span>Unverify ID</span>
          </Popconfirm>
        ),
      });
    }
    if (!record.is_active) {
      items.push({
        key: "activate",
        label: "Activate Account",
        onClick: () => handleActivatePatient(record),
      });
    }
    if (record.is_active) {
      items.push({
        key: "deactivate",
        label: (
          <Popconfirm
            title="Deactivate this account?"
            description="The patient will lose access immediately."
            onConfirm={() => handleDeactivatePatient(record)}
            okText="Deactivate" okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onClick={e => e.stopPropagation()}>
            <span style={{ color: "#ff4d4f" }}>Deactivate Account</span>
          </Popconfirm>
        ),
      });
    }
    items.push(
      { key: "reset", label: "Reset Password", onClick: () => handleResetPassword(record, "patient") },
      { key: "view",  label: "View Details",   onClick: () => { setDetailRecord(record); setDetailOpen(true); } },
    );
    return items;
  };

  // ── provider dropdown actions ─────────────────────────────────────────────
  const providerDropdownItems = (record, setList) => {
    const items = [];
    if (!record.is_active && !record.rejection_reason) {
      items.push({
        key: "approve",
        label: <span style={{ color: "#52c41a" }}>Approve</span>,
        onClick: () => handleApproveProvider(record, setList),
      });
    }
    if (!record.is_active) {
      items.push({
        key: "activate",
        label: "Activate Account",
        onClick: () => handleActivateProvider(record, setList),
      });
    }
    if (record.is_active) {
      items.push({
        key: "deactivate",
        label: (
          <Popconfirm
            title="Deactivate this provider?"
            description="They will lose access immediately."
            onConfirm={() => handleDeactivateProvider(record, setList)}
            okText="Deactivate" okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onClick={e => e.stopPropagation()}>
            <span style={{ color: "#ff4d4f" }}>Deactivate Account</span>
          </Popconfirm>
        ),
      });
    }
    items.push({
      key: "reset",
      label: "Reset Password",
      onClick: () => handleResetPassword(record, "provider"),
    });
    return items;
  };

  // ── filtered lists ────────────────────────────────────────────────────────
  const filteredPatients = patients.filter(p => {
    const q = patientSearch.trim().toLowerCase();
    if (q) {
      const name = `${p.first_name ?? ""} ${p.last_name ?? ""}`.toLowerCase();
      if (!name.includes(q) && !p.email?.toLowerCase().includes(q) && !p.pin?.includes(q))
        return false;
    }
    if (patientFilter === "active"     && !p.is_active)             return false;
    if (patientFilter === "inactive"   && p.is_active)              return false;
    if (patientFilter === "unverified" && p.government_id_verified) return false;
    return true;
  });

  const filteredDoctors = doctors.filter(d => {
    if (doctorFilter === "active"   && !d.is_active)                     return false;
    if (doctorFilter === "inactive" && d.is_active)                      return false;
    if (doctorFilter === "pending"  && d.approval_status !== "PENDING")  return false;
    return true;
  });

  const filteredPharmacists = pharmacists.filter(p => {
    if (pharmacistFilter === "active"   && !p.is_active)                     return false;
    if (pharmacistFilter === "inactive" && p.is_active)                      return false;
    if (pharmacistFilter === "pending"  && p.approval_status !== "PENDING")  return false;
    return true;
  });

  // ── recent registrations (derived from loaded data) ───────────────────────
  const recentRegistrations = [
    ...patients.map(p => ({ name: fullName(p), role: "PATIENT", date: p.date_joined, is_active: p.is_active })),
    ...doctors.map(d => ({ name: fullName(d), role: "DOCTOR", date: d.date_joined, is_active: d.is_active })),
    ...pharmacists.map(p => ({ name: fullName(p), role: "PHARMACIST", date: p.date_joined, is_active: p.is_active })),
  ]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 10)
    .map((r, i) => ({ ...r, key: i }));

  // ── column defs ───────────────────────────────────────────────────────────
  const PATIENT_COLS = [
    { title: "Name",      key: "name",      render: (_, r) => fullName(r) },
    { title: "Email",     dataIndex: "email",key: "email",   ellipsis: true },
    { title: "PIN",       dataIndex: "pin", key: "pin",      width: 130 },
    { title: "ID Status", key: "id_status", width: 110,      render: (_, r) => idStatusTag(r) },
    { title: "Account",   key: "account",   width: 100,
      render: (_, r) => r.is_active
        ? <Tag color="success">Active</Tag>
        : <Tag color="error">Inactive</Tag>,
    },
    { title: "Joined",    dataIndex: "date_joined", key: "date_joined", width: 110, render: fmtDate },
    { title: "Actions",   key: "actions",  width: 70,
      render: (_, record) => (
        <Dropdown
          menu={{ items: patientDropdownItems(record) }}
          trigger={["click"]}
          placement="bottomRight">
          <Button size="small" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const providerColumns = (setList) => [
    { title: "Name",     key: "name",      render: (_, r) => fullName(r) },
    { title: "Email",    dataIndex: "email",key: "email",    ellipsis: true },
    { title: "Specialty",dataIndex: "specialty",key: "specialty",width: 150,
      render: v => v ?? <Text type="secondary">—</Text>,
    },
    { title: "Status",   key: "account",   width: 100,
      render: (_, r) => r.is_active
        ? <Tag color="success">Active</Tag>
        : <Tag color="default">Inactive</Tag>,
    },
    { title: "Approval", key: "approval_status", width: 100,
      render: (_, r) => {
        if (r.is_active) return <Tag color="success">Approved</Tag>;
        if (r.rejection_reason) return <Tag color="error">Rejected</Tag>;
        return <Tag color="warning">Pending</Tag>;
      },
    },
    { title: "Joined",   dataIndex: "date_joined",key: "date_joined",width: 110,render: fmtDate },
    { title: "Actions",  key: "actions",  width: 70,
      render: (_, record) => (
        <Dropdown
          menu={{ items: providerDropdownItems(record, setList) }}
          trigger={["click"]}
          placement="bottomRight">
          <Button size="small" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const fetchSignupRequests = () => {
    setSignupLoaded(false); // triggers the useEffect to re-fetch
  };

  const handleApproveSignup = async () => {
    try {
      await approveSignupRequest(approveModal.id, {});
      message.success(`Account created for ${approveModal.email}`);
      setApproveModal(null);
      setSignupRows(prev => prev.map(r => r.id === approveModal.id ? { ...r, status: "APPROVED" } : r));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Approval failed.");
    }
  };

  const handleRejectSignup = async () => {
    try {
      await rejectSignupRequest(signupRejectModal.id, rejectReason);
      message.success("Request rejected.");
      setSignupRejectModal(null);
      setRejectReason("");
      setSignupRows(prev => prev.map(r => r.id === signupRejectModal.id ? { ...r, status: "REJECTED" } : r));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Rejection failed.");
    }
  };

  // ── hospital handlers ────────────────────────────────────────────────────
  const handleCreateHospital = async (values) => {
    setSubmitting(true);
    try {
      const res = await createHospital(values);
      setHospitals(prev => [{ ...res.data, key: res.data.id }, ...prev]);
      message.success("Hospital created.");
      setHospitalModal(false);
      hospitalForm.resetFields();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.name?.[0] ?? d?.detail ?? "Failed to create hospital.");
    } finally { setSubmitting(false); }
  };

  const openHospitalDetail = async (h) => {
    setHospitalDetail(h);
    setHospitalDocs([]);
    try {
      const res = await getHospitalDoctors(h.id);
      setHospitalDocs(toArray(res.data));
    } catch {
      setHospitalDocs([]);
    }
  };

  const handleAssignDoctor = async (values) => {
    setSubmitting(true);
    try {
      await assignDoctorToHospital(assignDocTarget.id, values.doctor_profile_id);
      message.success("Doctor assigned to hospital.");
      const res = await getHospitalDoctors(assignDocTarget.id);
      setHospitalDocs(toArray(res.data));
      setAssignDocModal(false);
      assignDocForm.resetFields();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to assign doctor.");
    } finally { setSubmitting(false); }
  };

  const handleRemoveDoctor = async (hospitalId, doctorProfileId) => {
    try {
      await removeDoctorFromHospital(hospitalId, doctorProfileId);
      setHospitalDocs(prev => prev.filter(d => d.profile_id !== doctorProfileId));
      message.success("Doctor removed from hospital.");
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to remove doctor.");
    }
  };

  // ── bulk registration handlers ────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadBulkTemplate();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "bulk_registration_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error("Failed to download template.");
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) { message.warning("Please select a CSV file first."); return; }
    setBulkLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", bulkFile);
      const res = await bulkRegisterProviders(formData);
      setBulkResult(res.data);
      const count = res.data.created?.length ?? 0;
      message.success(`Bulk registration complete. ${count} account${count !== 1 ? "s" : ""} created.`);
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Bulk registration failed.");
    } finally { setBulkLoading(false); }
  };

  const downloadBulkResults = () => {
    if (!bulkResult?.created?.length) return;
    const rows = [
      ["First Name", "Last Name", "Email", "Role", "Temp Password"],
      ...bulkResult.created.map(r => [r.first_name, r.last_name, r.email, r.role, r.temp_password]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "bulk_registration_results.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ── views ─────────────────────────────────────────────────────────────────
  function renderContent() {
    switch (selectedMenu) {
      case "signup-requests": {
        const pending  = signupRows.filter(r => r.status === "PENDING");
        const reviewed = signupRows.filter(r => r.status !== "PENDING");
        const STATUS_COLOR = { PENDING: "gold", APPROVED: "green", REJECTED: "red" };
        const ROLE_COLOR   = { DOCTOR: "blue", PATIENT: "green", PHARMACIST: "purple" };
        const cols = [
          { title: "Name",         key: "name",         render: (_, r) => `${r.first_name} ${r.last_name}` },
          { title: "Email",        dataIndex: "email",  key: "email" },
          { title: "Role",         dataIndex: "role",   key: "role",    width: 110, render: v => <Tag color={ROLE_COLOR[v] ?? "default"}>{v}</Tag> },
          { title: "Organisation", key: "org",          width: 160,     render: (_, r) => r.organisation_name ?? <Text type="secondary">Not specified</Text> },
          { title: "Submitted",    dataIndex: "created_at", key: "created_at", width: 120, render: v => v ? new Date(v).toLocaleDateString() : "—" },
          { title: "Status",       dataIndex: "status", key: "status",  width: 100, render: s => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
          { title: "Actions",      key: "actions",      width: 160,
            render: (_, r) => r.status !== "PENDING" ? null : (
              <Space>
                <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                  onClick={() => setApproveModal(r)}>Approve</Button>
                <Button size="small" danger icon={<CloseCircleOutlined />}
                  onClick={() => { setSignupRejectModal(r); setRejectReason(""); }}>Reject</Button>
              </Space>
            ),
          },
        ];
        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <Title level={4} style={{ margin: 0 }}>Signup Requests</Title>
              <Button icon={<ReloadOutlined />} onClick={fetchSignupRequests} loading={signupLoading}>Refresh</Button>
            </div>
            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
              title={<Badge count={pending.length} offset={[8, 0]}><Text strong>Pending Review</Text></Badge>}>
              <Table columns={cols} dataSource={pending} loading={signupLoading} size="small"
                pagination={false} locale={{ emptyText: "No pending signup requests" }} />
            </Card>
            <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>Reviewed</Text>}>
              <Table columns={cols} dataSource={reviewed} loading={signupLoading} size="small"
                pagination={{ pageSize: 10 }} locale={{ emptyText: "No reviewed requests yet" }} />
            </Card>

            <Modal title="Approve Account Request" open={!!approveModal}
              onOk={handleApproveSignup} onCancel={() => setApproveModal(null)}
              okText="Create Account" okButtonProps={{ type: "primary" }}>
              {approveModal && (
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Name">{approveModal.first_name} {approveModal.last_name}</Descriptions.Item>
                  <Descriptions.Item label="Email">{approveModal.email}</Descriptions.Item>
                  <Descriptions.Item label="Role"><Tag color={ROLE_COLOR[approveModal.role]}>{approveModal.role}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Requested Org">{approveModal.organisation_name ?? "Not specified"}</Descriptions.Item>
                  {approveModal.notes && <Descriptions.Item label="Notes">{approveModal.notes}</Descriptions.Item>}
                </Descriptions>
              )}
            </Modal>

            <Modal title="Reject Signup Request" open={!!signupRejectModal}
              onOk={handleRejectSignup} onCancel={() => setSignupRejectModal(null)}
              okText="Reject" okButtonProps={{ danger: true }}>
              {signupRejectModal && (
                <>
                  <Text>Rejecting <strong>{signupRejectModal.email}</strong> ({signupRejectModal.role})</Text>
                  <Form layout="vertical" style={{ marginTop: 12 }}>
                    <Form.Item label="Reason (optional)">
                      <Input.TextArea rows={3} value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="e.g. Invalid license number…" />
                    </Form.Item>
                  </Form>
                </>
              )}
            </Modal>
          </>
        );
      }

      case "overview": {
        const statCards = [
          { title: "Total Patients",    value: stats.total_patients ?? 0,            color: "#1677ff" },
          { title: "Active Patients",   value: stats.active_patients ?? 0,            color: "#52c41a" },
          { title: "Unverified IDs",    value: stats.unverified_ids ?? 0,             color: "#fa8c16" },
          { title: "Pending Approvals", value: stats.pending_provider_approvals ?? 0, color: "#f5222d" },
        ];
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Operations Overview</Title>

            {/* Org info banner */}
            {myOrg && (
              <Card
                variant="borderless"
                style={{ marginBottom: 16, borderRadius: 8, background: "#f0f5ff", border: "1px solid #adc6ff" }}>
                <Row align="middle" gutter={16}>
                  <Col>
                    <Avatar size={40} icon={<BankOutlined />} style={{ background: "#722ed1" }} />
                  </Col>
                  <Col flex={1}>
                    <Text strong style={{ fontSize: 16, color: "#1677ff" }}>{myOrg.name}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {myOrg.contact_person_name} &bull; {myOrg.contact_email}
                      </Text>
                    </div>
                  </Col>
                  <Col>
                    <Tag color={myOrg.is_active ? "success" : "error"}>
                      {myOrg.is_active ? "Contract Active" : "Inactive"}
                    </Tag>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Stat cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              {statCards.map(({ title, value, color }) => (
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic
                      title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>{title}</Text>}
                      value={value}
                      styles={{ content: { color, fontSize: 28, fontWeight: 600 } }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>

            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}>
              <Title level={5} style={{ marginBottom: 16 }}>Quick Actions</Title>
              <Space wrap>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEnrollOpen(true)}>
                  Enroll New Patient
                </Button>
                {(!myOrg?.subscribed_modules?.length || myOrg.subscribed_modules.includes("DOCTOR")) && (
                  <Button icon={<PlusOutlined />} onClick={() => setDoctorRegOpen(true)}>
                    Register New Doctor
                  </Button>
                )}
                {(!myOrg?.subscribed_modules?.length || myOrg.subscribed_modules.includes("PHARMACY")) && (
                  <Button icon={<PlusOutlined />} onClick={() => setPharmacistRegOpen(true)}>
                    Register New Pharmacist
                  </Button>
                )}
                <Button
                  icon={<TeamOutlined />}
                  onClick={() => { setDoctorFilter("pending"); setSelectedMenu("doctors"); }}>
                  Approve Pending Providers
                </Button>
              </Space>
              <Title level={5} style={{ marginBottom: 12, marginTop: 20, color: "#595959", fontWeight: 500 }}>
                Provider Overview
              </Title>
              <Row gutter={[16, 16]}>
                {[
                  { label: "Total Doctors",    value: stats.total_doctors ?? 0 },
                  { label: "Total Pharmacists", value: stats.total_pharmacists ?? 0 },
                  { label: "Active Providers",  value: stats.active_providers ?? 0 },
                  { label: "Inactive Patients", value: stats.inactive_patients ?? 0 },
                ].map(({ label, value }) => (
                  <Col xs={12} sm={6} key={label}>
                    <Card size="small" variant="borderless"
                      style={{ background: "#f5f7fa", borderRadius: 6, textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 600, color: "#262626" }}>{value}</div>
                      <div style={{ fontSize: 12, color: "#8c8c8c" }}>{label}</div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>

            {/* Recent registrations */}
            {recentRegistrations.length > 0 && (
              <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
                title={<Text strong>Recent Registrations</Text>}>
                <Table
                  size="small"
                  dataSource={recentRegistrations}
                  pagination={false}
                  columns={[
                    { title: "Name",     dataIndex: "name",     key: "name" },
                    { title: "Role",     dataIndex: "role",     key: "role",
                      render: r => <Tag color="blue">{r}</Tag> },
                    { title: "Registered", dataIndex: "date",   key: "date",     width: 120, render: fmtDate },
                    { title: "Status",   dataIndex: "is_active", key: "status",  width: 90,
                      render: v => v ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag> },
                  ]}
                  locale={{ emptyText: "No registrations yet" }}
                />
              </Card>
            )}

            {/* This Month's Activity */}
            {stats.billing_this_month && (
              <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
                title={<Text strong>This Month's Activity</Text>}>
                <Row gutter={[16, 16]}>
                  {[
                    { label: "Patient Registrations", value: stats.billing_this_month.registrations ?? 0 },
                    { label: "Prescriptions Issued",  value: stats.billing_this_month.prescriptions ?? 0 },
                    { label: "Prescriptions Dispensed", value: stats.billing_this_month.dispenses ?? 0 },
                    { label: "Eye Examinations",      value: stats.billing_this_month.eye_examinations ?? 0 },
                    { label: "Lens Prescriptions",    value: stats.billing_this_month.lens_prescriptions ?? 0 },
                  ].map(({ label, value }) => (
                    <Col xs={12} sm={8} md={4} key={label}>
                      <Card size="small" variant="borderless"
                        style={{ background: "#f5f7fa", borderRadius: 6, textAlign: "center" }}>
                        <div style={{ fontSize: 22, fontWeight: 600, color: "#262626" }}>{value}</div>
                        <div style={{ fontSize: 11, color: "#8c8c8c" }}>{label}</div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card>
            )}

            {/* Billing note */}
            <Alert
              type="info"
              showIcon
              message="Billing"
              description={
                <Text style={{ fontSize: 13 }}>
                  You are billed by MCHUSA based on your organisation's activity. Contact{" "}
                  <Text strong>info@mchusa.com</Text> or <Text strong>+1 732-322-1902</Text> for your monthly invoice.
                </Text>
              }
            />
          </>
        );
      }

      case "my-org": {
        const outstandingCount = myInvoices.filter(inv => inv.status === "SENT" || inv.status === "OVERDUE").length;
        const INVOICE_STATUS_BADGE = {
          DRAFT:   { color: "default",  text: "Draft"   },
          SENT:    { color: "blue",     text: "Sent"    },
          VIEWED:  { color: "purple",   text: "Viewed"  },
          PAID:    { color: "success",  text: "Paid"    },
          OVERDUE: { color: "error",    text: "Overdue" },
        };
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Organisation</Title>
            {!myOrg ? (
              <Alert
                type="warning"
                showIcon
                message="No organisation assigned"
                description="You are not assigned to an organisation. Contact MCHUSA at info@mchusa.com."
              />
            ) : (
              <>
                <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}>
                  <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                      <Avatar size={48} icon={<BankOutlined />} style={{ background: "#722ed1" }} />
                    </Col>
                    <Col flex={1}>
                      <Title level={4} style={{ margin: 0 }}>{myOrg.name}</Title>
                      <Tag color={myOrg.is_active ? "success" : "error"} style={{ marginTop: 4 }}>
                        {myOrg.is_active ? "Active" : "Inactive"}
                      </Tag>
                    </Col>
                  </Row>

                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label="Contact Person">{myOrg.contact_person_name}</Descriptions.Item>
                    <Descriptions.Item label="Contact Email">{myOrg.contact_email}</Descriptions.Item>
                    <Descriptions.Item label="Contact Phone">{myOrg.contact_phone || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Contract Start">
                      {myOrg.contract_start_date ? new Date(myOrg.contract_start_date).toLocaleDateString() : "—"}
                    </Descriptions.Item>
                    {myOrg.contract_end_date && (
                      <Descriptions.Item label="Contract End">
                        {new Date(myOrg.contract_end_date).toLocaleDateString()}
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  <Alert
                    style={{ marginTop: 16 }}
                    type="info"
                    showIcon
                    message="Read-only"
                    description="To update organisation details, contact MCHUSA at info@mchusa.com"
                  />
                </Card>

                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  {[
                    { label: "Total Users",  value: myOrg.total_registered_users ?? 0, color: "#1677ff" },
                    { label: "Patients",     value: myOrg.patients ?? 0,               color: "#52c41a" },
                    { label: "Doctors",      value: myOrg.doctors ?? 0,                color: "#722ed1" },
                    { label: "Pharmacists",  value: myOrg.pharmacists ?? 0,            color: "#fa8c16" },
                  ].map(({ label, value, color }) => (
                    <Col xs={24} sm={12} lg={6} key={label}>
                      <Card variant="borderless" style={{ borderRadius: 8 }}>
                        <Statistic
                          title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>{label}</Text>}
                          value={value}
                          styles={{ content: { color, fontSize: 28, fontWeight: 600 } }}
                        />
                      </Card>
                    </Col>
                  ))}
                  <Col xs={24} sm={12} lg={6}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Statistic
                        title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Outstanding Invoices</Text>}
                        value={outstandingCount}
                        prefix={<FileTextOutlined />}
                        styles={{ content: { color: outstandingCount > 0 ? "#fa8c16" : "#52c41a", fontSize: 28, fontWeight: 600 } }}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* My Modules */}
                {(myOrg.subscribed_modules ?? []).length > 0 && (
                  <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}>
                    <Text strong style={{ display: "block", marginBottom: 10 }}>Subscribed Modules</Text>
                    <Space wrap>
                      {(myOrg.subscribed_modules).map(m => (
                        <Tag key={m} color="green" style={{ fontSize: 13, padding: "4px 10px" }}>
                          {m.charAt(0) + m.slice(1).toLowerCase().replace(/_/g, " ")}
                        </Tag>
                      ))}
                    </Space>
                    <Alert
                      style={{ marginTop: 12 }}
                      type="info"
                      showIcon
                      message={<Text style={{ fontSize: 12 }}>Contact <Text strong>info@mchusa.com</Text> to add or remove modules.</Text>}
                    />
                  </Card>
                )}

                {/* My Invoices */}
                <Card
                  variant="borderless"
                  style={{ borderRadius: 8 }}
                  title={<Text strong>My Invoices</Text>}
                >
                  <Table
                    size="small"
                    loading={invoicesLoading}
                    dataSource={myInvoices.map(inv => ({ ...inv, key: inv.id }))}
                    pagination={{ pageSize: 10, hideOnSinglePage: true }}
                    locale={{ emptyText: "No invoices yet. Your first invoice will appear here once MCHUSA generates it." }}
                    columns={[
                      { title: "Invoice #",  dataIndex: "invoice_number", key: "num",    ellipsis: true },
                      { title: "Period",     dataIndex: "period_label",   key: "period", width: 120 },
                      { title: "Total Due",  dataIndex: "total_amount",   key: "amount", width: 100, align: "right",
                        render: v => <Text strong>${parseFloat(v).toFixed(2)}</Text> },
                      { title: "Status",     dataIndex: "status",         key: "status", width: 85,
                        render: s => {
                          const b = INVOICE_STATUS_BADGE[s] ?? { color: "default", text: s };
                          return <Tag color={b.color} style={{ fontSize: 11 }}>{b.text}</Tag>;
                        }},
                      { title: "Due Date",   dataIndex: "due_date",       key: "due",    width: 100,
                        render: v => v ? new Date(v).toLocaleDateString() : "—" },
                      { title: "",           key: "actions",              width: 60,
                        render: (_, inv) => (
                          <Button size="small" icon={<FileTextOutlined />} onClick={() => openInvoiceDetail(inv)}>
                            View
                          </Button>
                        )},
                    ]}
                  />
                </Card>
              </>
            )}
          </>
        );
      }

      case "patients":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Patient Management</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={
                <Space wrap>
                  <Input.Search
                    placeholder="Search name, email, or PIN"
                    value={patientSearch}
                    onChange={e => setPatientSearch(e.target.value)}
                    allowClear
                    style={{ width: 260 }}
                  />
                  <Select value={patientFilter} onChange={setPatientFilter}
                    size="middle" style={{ width: 150 }}>
                    <Option value="all">All ({patients.length})</Option>
                    <Option value="active">Active ({patients.filter(p => p.is_active).length})</Option>
                    <Option value="inactive">Inactive ({patients.filter(p => !p.is_active).length})</Option>
                    <Option value="unverified">Unverified ID ({patients.filter(p => !p.government_id_verified).length})</Option>
                  </Select>
                </Space>
              }
              extra={
                <Button type="primary" size="small" icon={<PlusOutlined />}
                  onClick={() => setEnrollOpen(true)}>
                  Enroll Patient
                </Button>
              }>
              <Table
                columns={PATIENT_COLS}
                dataSource={filteredPatients}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "No patients found" }}
                size="small"
                onRow={r => ({ style: r.is_active ? {} : { opacity: 0.6 } })}
              />
            </Card>
          </>
        );

      case "doctors":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Doctor Management</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={
                <Select value={doctorFilter} onChange={setDoctorFilter}
                  size="middle" style={{ width: 170 }}>
                  <Option value="all">All ({doctors.length})</Option>
                  <Option value="active">Active ({doctors.filter(d => d.is_active).length})</Option>
                  <Option value="inactive">Inactive ({doctors.filter(d => !d.is_active).length})</Option>
                  <Option value="pending">Pending Approval ({doctors.filter(d => d.approval_status === "PENDING").length})</Option>
                </Select>
              }
              extra={
                <Button type="primary" size="small" icon={<PlusOutlined />}
                  onClick={() => setDoctorRegOpen(true)}>
                  Register New Doctor
                </Button>
              }>
              <Table
                columns={providerColumns(setDoctors)}
                dataSource={filteredDoctors}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "No doctors found" }}
                size="small"
                onRow={r => ({ style: r.is_active ? {} : { opacity: 0.6 } })}
              />
            </Card>
          </>
        );

      case "pharmacists":
        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Pharmacy Management</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={
                <Select value={pharmacistFilter} onChange={setPharmacistFilter}
                  size="middle" style={{ width: 170 }}>
                  <Option value="all">All ({pharmacists.length})</Option>
                  <Option value="active">Active ({pharmacists.filter(p => p.is_active).length})</Option>
                  <Option value="inactive">Inactive ({pharmacists.filter(p => !p.is_active).length})</Option>
                  <Option value="pending">Pending Approval ({pharmacists.filter(p => p.approval_status === "PENDING").length})</Option>
                </Select>
              }
              extra={
                <Button type="primary" size="small" icon={<PlusOutlined />}
                  onClick={() => setPharmacistRegOpen(true)}>
                  Register New Pharmacist
                </Button>
              }>
              <Table
                columns={providerColumns(setPharmacists)}
                dataSource={filteredPharmacists}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "No pharmacists found" }}
                size="small"
                onRow={r => ({ style: r.is_active ? {} : { opacity: 0.6 } })}
              />
            </Card>
          </>
        );

      case "support": {
        const filteredTickets = ticketFilter === "ALL"
          ? tickets : tickets.filter(t => t.status === ticketFilter);
        const filteredInbox = inboxFilter === "ALL"
          ? inboxTickets : inboxTickets.filter(t => t.status === inboxFilter);
        const inboxPending = inboxTickets.filter(t => t.status === "OPEN").length;

        const MY_TICKET_COLS = [
          { title: "Ticket #", dataIndex: "id", key: "id", width: 100,
            render: v => <Text code style={{ fontSize: 11 }}>{String(v).slice(0,8)}</Text> },
          { title: "Subject",  dataIndex: "subject",  key: "subject",  ellipsis: true },
          { title: "Category", dataIndex: "category", key: "category", width: 150,
            render: c => <Tag color="geekblue" style={{ fontSize: 11 }}>{CATEGORY_LABEL[c] ?? c}</Tag> },
          { title: "Priority", dataIndex: "priority", key: "priority", width: 90,
            render: p => <Tag color={PRIORITY_COLOR[p] ?? "default"} style={{ fontSize: 11 }}>{PRIORITY_LABEL[p] ?? p}</Tag> },
          { title: "Status",   dataIndex: "status",   key: "status",   width: 110,
            render: s => <Tag color={TICKET_STATUS_COLOR[s] ?? "default"} style={{ fontSize: 11 }}>{s?.replace(/_/g," ")}</Tag> },
          { title: "Submitted",dataIndex: "created_at",key: "date",   width: 110, render: fmtDate },
          { title: "", key: "actions", width: 70,
            render: (_, r) => <Button size="small" onClick={() => setTicketDetail(r)}>View</Button> },
        ];

        const INBOX_COLS = [
          { title: "Ticket #", dataIndex: "id", key: "id", width: 100,
            render: v => <Text code style={{ fontSize: 11 }}>{String(v).slice(0,8)}</Text> },
          { title: "From",  dataIndex: "submitted_by_email", key: "from", ellipsis: true,
            render: e => <Text style={{ fontSize: 12 }}>{e}</Text> },
          { title: "Subject", dataIndex: "subject", key: "subject", ellipsis: true },
          { title: "Priority", dataIndex: "priority", key: "priority", width: 90,
            render: p => <Tag color={PRIORITY_COLOR[p] ?? "default"} style={{ fontSize: 11 }}>{PRIORITY_LABEL[p] ?? p}</Tag> },
          { title: "Status", dataIndex: "status", key: "status", width: 110,
            render: s => <Tag color={TICKET_STATUS_COLOR[s] ?? "default"} style={{ fontSize: 11 }}>{s?.replace(/_/g," ")}</Tag> },
          { title: "Submitted", dataIndex: "created_at", key: "date", width: 110, render: fmtDate },
          { title: "", key: "actions", width: 170,
            render: (_, r) => {
              const canRespond  = r.status === "OPEN" || r.status === "IN_PROGRESS";
              const canEscalate = r.status === "OPEN" || r.status === "IN_PROGRESS";
              if (!canRespond && !canEscalate) return null;
              return (
                <Space size={4}>
                  {canRespond && (
                    <Button size="small" type={r.status === "OPEN" ? "primary" : "default"}
                      onClick={() => { setRespondTarget(r); setRespondOpen(true); }}>
                      {r.status === "OPEN" ? "Respond" : "Update"}
                    </Button>
                  )}
                  {canEscalate && (
                    <Button size="small" danger
                      onClick={() => { setEscalateTarget(r); setEscalateOpen(true); }}>
                      Escalate
                    </Button>
                  )}
                </Space>
              );
            },
          },
        ];

        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Support Tickets</Title>
            <Tabs
              activeKey={supportTab}
              onChange={setSupportTab}
              type="card"
              items={[
                {
                  key: "my-tickets",
                  label: "My Tickets to MCHUSA",
                  children: (
                    <Card variant="borderless" style={{ borderRadius: 8 }}
                      extra={
                        <Button type="primary" size="small" icon={<PlusOutlined />}
                          onClick={() => setTicketCreateOpen(true)}>
                          New Ticket
                        </Button>
                      }>
                      <Tabs activeKey={ticketFilter} onChange={setTicketFilter}
                        style={{ marginBottom: 8 }}
                        items={[
                          { key: "ALL",         label: `All (${tickets.length})` },
                          { key: "OPEN",        label: `Open (${tickets.filter(t=>t.status==="OPEN").length})` },
                          { key: "IN_PROGRESS", label: `In Progress (${tickets.filter(t=>t.status==="IN_PROGRESS").length})` },
                          { key: "RESOLVED",    label: `Resolved (${tickets.filter(t=>t.status==="RESOLVED").length})` },
                          { key: "CLOSED",      label: `Closed (${tickets.filter(t=>t.status==="CLOSED").length})` },
                        ]}
                      />
                      <Table size="small" dataSource={filteredTickets} columns={MY_TICKET_COLS}
                        pagination={{ pageSize: 15, hideOnSinglePage: true }}
                        locale={{ emptyText: (
                          <div style={{ textAlign: "center", padding: "32px 0" }}>
                            <CustomerServiceOutlined style={{ fontSize: 36, color: "#d9d9d9", display: "block", marginBottom: 8 }} />
                            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                              No support tickets yet. Use "New Ticket" to contact MCHUSA for billing
                              questions, technical issues, or account management.
                            </Text>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => setTicketCreateOpen(true)}>
                              New Ticket
                            </Button>
                          </div>
                        )}}
                      />
                    </Card>
                  ),
                },
                {
                  key: "inbox",
                  label: inboxPending > 0
                    ? <Badge count={inboxPending} size="small" offset={[6,0]}>User Inbox</Badge>
                    : "User Inbox",
                  children: (
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Alert type="info" showIcon style={{ marginBottom: 12 }}
                        message="Tickets submitted by your organisation's end users. Respond or escalate to MCHUSA." />
                      <Tabs activeKey={inboxFilter} onChange={setInboxFilter}
                        style={{ marginBottom: 8 }}
                        items={[
                          { key: "ALL",         label: `All (${inboxTickets.length})` },
                          { key: "OPEN",        label: `Open (${inboxTickets.filter(t=>t.status==="OPEN").length})` },
                          { key: "IN_PROGRESS", label: `In Progress (${inboxTickets.filter(t=>t.status==="IN_PROGRESS").length})` },
                          { key: "RESOLVED",    label: `Resolved (${inboxTickets.filter(t=>t.status==="RESOLVED").length})` },
                          { key: "ESCALATED",   label: `Escalated (${inboxTickets.filter(t=>t.status==="ESCALATED").length})` },
                        ]}
                      />
                      <Table size="small" dataSource={filteredInbox} columns={INBOX_COLS}
                        pagination={{ pageSize: 15, hideOnSinglePage: true }}
                        locale={{ emptyText: (
                          <div style={{ textAlign: "center", padding: "32px 0" }}>
                            <CustomerServiceOutlined style={{ fontSize: 36, color: "#d9d9d9", display: "block", marginBottom: 8 }} />
                            <Text type="secondary">No user tickets in your inbox.</Text>
                          </div>
                        )}}
                      />
                    </Card>
                  ),
                },
              ]}
            />
          </>
        );
      }

      case "profile":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Profile</Title>
            <Card variant="borderless" style={{ borderRadius: 8, maxWidth: 480 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {[
                    ["Email",        user?.email ?? "—"],
                    ["Role",         <Tag color="purple">Operations Manager</Tag>],
                    ["Organisation", myOrg?.name
                      ? <Tag color="blue">{myOrg.name}</Tag>
                      : <Text type="secondary">Not assigned</Text>],
                  ].map(([label, val]) => (
                    <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "10px 12px", color: "#8c8c8c", width: "35%" }}>{label}</td>
                      <td style={{ padding: "10px 12px" }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            <ChangePasswordForm />
          </>
        );

      case "hospitals": {
        const hospitalCols = [
          { title: "Hospital",   dataIndex: "name",         key: "name" },
          { title: "Phone",      dataIndex: "phone",        key: "phone",   width: 130, render: v => v || "—" },
          { title: "Email",      dataIndex: "email",        key: "email",   width: 180, render: v => v || "—" },
          { title: "Doctors",    dataIndex: "doctor_count", key: "dc",      width: 80,  render: v => v ?? 0 },
          { title: "Status",     dataIndex: "is_active",    key: "status",  width: 90,
            render: v => <Tag color={v ? "success" : "default"}>{v ? "Active" : "Inactive"}</Tag> },
          { title: "Actions",    key: "actions",            width: 100,
            render: (_, r) => <Button size="small" onClick={() => openHospitalDetail(r)}>Detail</Button> },
        ];
        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <Title level={4} style={{ margin: 0 }}>Hospitals</Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setHospitalModal(true)}>
                Add Hospital
              </Button>
            </div>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              <Table columns={hospitalCols} dataSource={hospitals} loading={hospitalsLoading}
                size="small" pagination={{ pageSize: 10 }}
                locale={{ emptyText: "No hospitals registered yet. Click 'Add Hospital' to create one." }} />
            </Card>

            <Modal title="Add Hospital" open={hospitalModal}
              onCancel={() => { setHospitalModal(false); hospitalForm.resetFields(); }}
              onOk={() => hospitalForm.submit()} okText="Create" confirmLoading={submitting}>
              <Form form={hospitalForm} layout="vertical" onFinish={handleCreateHospital}>
                <Form.Item name="name" label="Hospital Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="address" label="Address">
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item name="phone" label="Phone"><Input /></Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="email" label="Email"><Input type="email" /></Form.Item>
                  </Col>
                </Row>
                <Form.Item name="licence_number" label="Licence Number">
                  <Input />
                </Form.Item>
              </Form>
            </Modal>

            <Drawer title={hospitalDetail?.name ?? "Hospital Detail"}
              open={!!hospitalDetail}
              onClose={() => { setHospitalDetail(null); setHospitalDocs([]); }}
              width={520}
              extra={
                <Button type="primary" icon={<PlusOutlined />}
                  onClick={() => { setAssignDocTarget(hospitalDetail); setAssignDocModal(true); }}>
                  Assign Doctor
                </Button>
              }>
              {hospitalDetail && (
                <>
                  <Descriptions column={1} size="small" style={{ marginBottom: 20 }}>
                    <Descriptions.Item label="Address">{hospitalDetail.address || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{hospitalDetail.phone || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Email">{hospitalDetail.email || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Licence">{hospitalDetail.licence_number || "—"}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={hospitalDetail.is_active ? "success" : "default"}>
                        {hospitalDetail.is_active ? "Active" : "Inactive"}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                  <Title level={5}>Assigned Doctors</Title>
                  <Table size="small" pagination={false}
                    dataSource={hospitalDocs.map(d => ({ ...d, key: d.profile_id ?? d.id }))}
                    locale={{ emptyText: "No doctors assigned" }}
                    columns={[
                      { title: "Name",       key: "name",    render: (_, r) => `${r.first_name} ${r.last_name}` },
                      { title: "Specialty",  dataIndex: "specialty", key: "spec", render: v => v || "—" },
                      { title: "",           key: "remove",  width: 80,
                        render: (_, r) => (
                          <Popconfirm title="Remove this doctor from the hospital?"
                            onConfirm={() => handleRemoveDoctor(hospitalDetail.id, r.profile_id)}
                            okText="Remove" okButtonProps={{ danger: true }}>
                            <Button size="small" danger>Remove</Button>
                          </Popconfirm>
                        ),
                      },
                    ]} />
                </>
              )}
            </Drawer>

            <Modal title={`Assign Doctor to ${assignDocTarget?.name ?? "Hospital"}`}
              open={assignDocModal}
              onCancel={() => { setAssignDocModal(false); assignDocForm.resetFields(); }}
              onOk={() => assignDocForm.submit()} okText="Assign" confirmLoading={submitting}>
              <Form form={assignDocForm} layout="vertical" onFinish={handleAssignDoctor}>
                <Form.Item name="doctor_profile_id" label="Doctor" rules={[{ required: true }]}>
                  <Select showSearch placeholder="Select a doctor" optionFilterProp="label"
                    options={doctors
                      .filter(d => d.doctor_profile_id)
                      .map(d => ({
                        value: d.doctor_profile_id,
                        label: `${d.first_name} ${d.last_name}`.trim() || d.email,
                      }))} />
                </Form.Item>
              </Form>
            </Modal>
          </>
        );
      }

      case "bulk-registration": {
        return (
          <>
            <Title level={4} style={{ marginBottom: 20 }}>Bulk Provider Registration</Title>
            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
              title="Step 1 — Download Template">
              <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                Download the CSV template, fill it in, then upload it below. Do not change the column headers.
              </Text>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                Download CSV Template
              </Button>
            </Card>

            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
              title="Step 2 — Upload Completed CSV">
              <Upload.Dragger accept=".csv" multiple={false}
                beforeUpload={(file) => { setBulkFile(file); setBulkResult(null); return false; }}
                onRemove={() => { setBulkFile(null); setBulkResult(null); }}
                fileList={bulkFile ? [{ uid: "1", name: bulkFile.name, status: "done" }] : []}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ fontSize: 36, color: "#1677ff" }} />
                </p>
                <p className="ant-upload-text">Click or drag a CSV file here</p>
                <p className="ant-upload-hint">Supports a single .csv file matching the template</p>
              </Upload.Dragger>
              <Button type="primary" style={{ marginTop: 16 }} loading={bulkLoading}
                disabled={!bulkFile} onClick={handleBulkUpload}>
                Upload and Register
              </Button>
            </Card>

            {bulkResult && (
              <Card variant="borderless" style={{ borderRadius: 8 }}
                title={
                  <Space>
                    <span>Results</span>
                    <Tag color="success">{bulkResult.created?.length ?? 0} created</Tag>
                    {(bulkResult.errors?.length ?? 0) > 0 && (
                      <Tag color="error">{bulkResult.errors.length} errors</Tag>
                    )}
                  </Space>
                }
                extra={
                  (bulkResult.created?.length ?? 0) > 0 && (
                    <Button icon={<DownloadOutlined />} onClick={downloadBulkResults}>
                      Download Credentials
                    </Button>
                  )
                }>
                {(bulkResult.created?.length ?? 0) > 0 && (
                  <>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>Created Accounts</Text>
                    <Table size="small" style={{ marginBottom: 16 }}
                      dataSource={bulkResult.created.map((r, i) => ({ ...r, key: i }))}
                      pagination={false}
                      columns={[
                        { title: "Name",     key: "name",          render: (_, r) => `${r.first_name} ${r.last_name}` },
                        { title: "Email",    dataIndex: "email",   key: "email" },
                        { title: "Role",     dataIndex: "role",    key: "role",  width: 120, render: v => <Tag>{v}</Tag> },
                        { title: "Password", dataIndex: "temp_password", key: "pwd", width: 130,
                          render: v => <Text code copyable>{v}</Text> },
                      ]} />
                  </>
                )}
                {(bulkResult.errors?.length ?? 0) > 0 && (
                  <>
                    <Text strong style={{ display: "block", marginBottom: 8, color: "#ff4d4f" }}>Errors</Text>
                    <Table size="small"
                      dataSource={bulkResult.errors.map((r, i) => ({ ...r, key: i }))}
                      pagination={false}
                      columns={[
                        { title: "Row",   dataIndex: "row",   key: "row",   width: 60 },
                        { title: "Email", dataIndex: "email", key: "email" },
                        { title: "Error", dataIndex: "error", key: "error" },
                      ]} />
                  </>
                )}
              </Card>
            )}
          </>
        );
      }

      default:
        return null;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}>
        <div style={{ height: 64, display: "flex", alignItems: "center",
          justifyContent: "center", borderBottom: "1px solid #f0f0f0", padding: "0 16px" }}>
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
              <Text strong style={{ color: "#722ed1", fontSize: 13, whiteSpace: "nowrap" }}>Digital Healthcare Network</Text>
              <Text style={{ color: "#722ed1", fontSize: 12, whiteSpace: "nowrap" }}>
                {user?.role === "ORGANISATION_ADMINISTRATOR" ? "Organisation Admin" : "Operations"}
              </Text>
            </div>
          )}
        </div>
        <Menu mode="inline" selectedKeys={[selectedMenu]}
          items={buildMenuItems(
            tickets.filter(t => t.status === "OPEN" || t.status === "IN_PROGRESS").length,
            signupRows.filter(r => r.status === "PENDING").length
          )}
          onClick={({ key }) => setSelectedMenu(key)}
          style={{ border: "none", marginTop: 8 }} />
      </Sider>

      <Layout>
        <Header style={{ background: "#fff", padding: "0 24px", display: "flex",
          alignItems: "center", justifyContent: "space-between",
          borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, zIndex: 100 }}>
          <div>
            <Text strong style={{ fontSize: 16, color: "#1677ff" }}>MCHUSA</Text>
            <Text style={{ fontSize: 14, color: "#8c8c8c", marginLeft: 8 }}>
              {myOrg ? `| ${myOrg.name}` : "| Operations Manager Portal"}
            </Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <OpsNotificationBell onNavigate={(section) => setSelectedMenu(section)} />
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <Avatar size={32} icon={<UserOutlined />} style={{ background: "#722ed1" }} />
                <Text style={{ fontSize: 14, color: "#262626" }}>{user?.email ?? "Ops Manager"}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ padding: 24, background: "#f5f7fa" }}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3, 4].map(i => (
                  <Col xs={24} sm={12} lg={6} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8 }}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            </>
          ) : error ? (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)} closable />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── Enroll Patient Modal ─────────────────────────────────────────── */}
      <Modal title="Enroll New Patient" open={enrollOpen}
        onOk={() => enrollForm.submit()}
        onCancel={() => { setEnrollOpen(false); enrollForm.resetFields(); }}
        okText="Enroll Patient" confirmLoading={submitting} destroyOnHidden keyboard width={560}>
        <Form form={enrollForm} layout="vertical" onFinish={handleEnrollPatient}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email"
            rules={[{ required: true }, { type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone Number">
            <Input placeholder="+1-868-555-0000" />
          </Form.Item>
          <Form.Item name="password" label="Password"
            rules={[{ required: true }, { min: 8, message: "Min 8 characters" }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="government_id_type" label="Government ID Type">
            <Select placeholder="Select type" allowClear>
              <Option value="PASSPORT">Passport</Option>
              <Option value="DRIVERS_LICENSE">Driver's License</Option>
              <Option value="NATIONAL_ID">National ID</Option>
              <Option value="OTHER">Other</Option>
            </Select>
          </Form.Item>
          <Form.Item name="government_id_number" label="Government ID Number">
            <Input />
          </Form.Item>
          <Form.Item name="government_id_photo" label="Government ID Photo">
            <Upload beforeUpload={() => false} maxCount={1} accept="image/*">
              <Button icon={<UploadOutlined />}>Upload Photo</Button>
            </Upload>
          </Form.Item>
          {myOrg && (
            <Alert
              type="info"
              showIcon
              message={`This patient will be registered under: ${myOrg.name}`}
              style={{ marginBottom: 0 }}
            />
          )}
        </Form>
      </Modal>

      {/* ── Patient Detail Drawer ────────────────────────────────────────── */}
      <Drawer
        title={detailRecord ? fullName(detailRecord) : "Patient Details"}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={420}>
        {detailRecord && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
              <tbody>
                {[
                  ["Email",         detailRecord.email],
                  ["PIN",           detailRecord.pin],
                  ["Account",       detailRecord.is_active
                    ? <Tag color="success">Active</Tag>
                    : <Tag color="error">Inactive</Tag>],
                  ["ID Status",     idStatusTag(detailRecord)],
                  ["ID Type",       detailRecord.government_id_type || "—"],
                  ["Organisation",  detailRecord.organisation_name
                    ? <Tag color="blue">{detailRecord.organisation_name}</Tag>
                    : <Text type="secondary">—</Text>],
                  ["Joined",        fmtDate(detailRecord.date_joined)],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 12px", color: "#8c8c8c", width: "40%" }}>{label}</td>
                    <td style={{ padding: "8px 12px" }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {detailRecord.government_id_photo_url && (
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>Government ID Photo</Text>
                <img
                  src={detailRecord.government_id_photo_url}
                  alt="Government ID"
                  style={{ width: "100%", borderRadius: 6, border: "1px solid #f0f0f0", userSelect: "none" }}
                  draggable={false}
                  onContextMenu={e => e.preventDefault()}
                />
              </div>
            )}

            <Space wrap>
              {detailRecord.government_id_photo_url && !detailRecord.government_id_verified && (
                <Button type="primary" icon={<CheckCircleOutlined />}
                  onClick={() => handleVerifyId(detailRecord)}>
                  Verify ID
                </Button>
              )}
              {detailRecord.government_id_verified && (
                <Popconfirm
                  title="Remove ID verification?"
                  onConfirm={() => handleUnverifyId(detailRecord)}
                  okText="Unverify" okButtonProps={{ danger: true }}>
                  <Button danger>Unverify ID</Button>
                </Popconfirm>
              )}
              {!detailRecord.is_active
                ? <Button onClick={() => handleActivatePatient(detailRecord)}>Activate</Button>
                : (
                  <Popconfirm
                    title="Deactivate this account?"
                    onConfirm={() => handleDeactivatePatient(detailRecord)}
                    okText="Deactivate" okButtonProps={{ danger: true }}>
                    <Button danger>Deactivate</Button>
                  </Popconfirm>
                )
              }
              <Button onClick={() => handleResetPassword(detailRecord, "patient")}>
                Reset Password
              </Button>
            </Space>
          </>
        )}
      </Drawer>

      {/* ── Register Doctor Modal ───────────────────────────────────────── */}
      <Modal title="Register New Doctor" open={doctorRegOpen}
        onOk={() => doctorRegForm.submit()}
        onCancel={() => { setDoctorRegOpen(false); doctorRegForm.resetFields(); }}
        okText="Register Doctor" confirmLoading={submitting} destroyOnHidden keyboard width={520}>
        <Form form={doctorRegForm} layout="vertical"
          onFinish={v => handleRegisterProvider(v, "DOCTOR", doctorRegForm, setDoctorRegOpen)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email"
            rules={[{ required: true }, { type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="specialty" label="Specialty" rules={[{ required: true }]}>
            <Input placeholder="e.g. General Practice, Cardiology" />
          </Form.Item>
          <Form.Item name="license_number" label="License Number">
            <Input />
          </Form.Item>
          {myOrg && (
            <Alert type="info" showIcon
              message={`Will be registered under: ${myOrg.name}`}
              style={{ marginBottom: 0 }} />
          )}
        </Form>
      </Modal>

      {/* ── Register Pharmacist Modal ────────────────────────────────────── */}
      <Modal title="Register New Pharmacist" open={pharmacistRegOpen}
        onOk={() => pharmacistRegForm.submit()}
        onCancel={() => { setPharmacistRegOpen(false); pharmacistRegForm.resetFields(); }}
        okText="Register Pharmacist" confirmLoading={submitting} destroyOnHidden keyboard width={520}>
        <Form form={pharmacistRegForm} layout="vertical"
          onFinish={v => handleRegisterProvider(v, "PHARMACIST", pharmacistRegForm, setPharmacistRegOpen)}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label="Email"
            rules={[{ required: true }, { type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="pharmacy_name" label="Pharmacy Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Caribbean Pharmacy" />
          </Form.Item>
          <Form.Item name="license_number" label="License Number">
            <Input />
          </Form.Item>
          {myOrg && (
            <Alert type="info" showIcon
              message={`Will be registered under: ${myOrg.name}`}
              style={{ marginBottom: 0 }} />
          )}
        </Form>
      </Modal>

      {/* ── Provider Registration Success Modal ──────────────────────────── */}
      <Modal
        title="Provider Registered"
        open={Boolean(providerRegResult)}
        onCancel={() => setProviderRegResult(null)}
        footer={<Button type="primary" onClick={() => setProviderRegResult(null)}>Done</Button>}
        keyboard destroyOnHidden>
        {providerRegResult && (
          <div>
            <Alert type="success" showIcon style={{ marginBottom: 16 }}
              message={`${providerRegResult.name} registered successfully.`} />
            <Text strong>Account Details</Text>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
              <tbody>
                {[
                  ["Email", providerRegResult.email],
                  ["Role",  providerRegResult.role],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 12px", color: "#8c8c8c", width: "35%" }}>{label}</td>
                    <td style={{ padding: "8px 12px" }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 12 }}>
              <Text strong>Temporary Password:</Text>
              <Typography.Text copyable
                style={{ display: "block", marginTop: 4, fontSize: 18, fontFamily: "monospace",
                  background: "#f5f5f5", padding: "8px 12px", borderRadius: 4 }}>
                {providerRegResult.temp_password}
              </Typography.Text>
            </div>
            <Alert type="warning" showIcon style={{ marginTop: 12 }}
              message="Share this password securely — it will not be shown again." />
          </div>
        )}
      </Modal>

      {/* ── Temp Password Modal ──────────────────────────────────────────── */}
      <Modal
        title="Temporary Password Generated"
        open={Boolean(resetPassResult)}
        onCancel={() => setResetPassResult(null)}
        footer={<Button onClick={() => setResetPassResult(null)}>Close</Button>}
        keyboard destroyOnHidden>
        {resetPassResult && (
          <div>
            <Alert type="warning" showIcon
              message="Share this password securely — it will not be shown again."
              style={{ marginBottom: 16 }} />
            <Text strong>User: </Text><Text>{resetPassResult.name}</Text><br />
            <div style={{ marginTop: 12 }}>
              <Text strong>Temporary Password:</Text>
              <Typography.Text copyable
                style={{ display: "block", marginTop: 4, fontSize: 18, fontFamily: "monospace",
                  background: "#f5f5f5", padding: "8px 12px", borderRadius: 4 }}>
                {resetPassResult.temp_password}
              </Typography.Text>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Respond to User Ticket Modal ── */}
      <Modal
        title={respondTarget ? `Respond — ${String(respondTarget.id).slice(0,8)}` : "Respond to Ticket"}
        open={respondOpen}
        onOk={() => respondForm.submit()}
        onCancel={() => { setRespondOpen(false); respondForm.resetFields(); setRespondTarget(null); }}
        okText="Send Response"
        confirmLoading={submitting}
        destroyOnHidden keyboard width={520}>
        {respondTarget && (
          <div>
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "#fafafa", borderRadius: 4 }}>
              <Text strong>{respondTarget.subject}</Text>
              <div style={{ marginTop: 4, fontSize: 13, color: "#595959" }}>{respondTarget.description}</div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                From: {respondTarget.submitted_by_email} &bull; {fmtDate(respondTarget.created_at)}
              </Text>
            </div>
            <Form form={respondForm} layout="vertical" onFinish={handleRespondToTicket}
              initialValues={{ status: "RESOLVED" }}>
              <Form.Item name="resolution_notes" label="Your Response"
                rules={[{ required: true, message: "Please enter a response" },
                        { min: 10, message: "At least 10 characters" }]}>
                <Input.TextArea rows={4} placeholder="Enter your resolution or update. Do not include PHI." />
              </Form.Item>
              <Form.Item name="status" label="Set Status">
                <Select>
                  <Select.Option value="IN_PROGRESS">In Progress — still working on it</Select.Option>
                  <Select.Option value="RESOLVED">Resolved — issue addressed</Select.Option>
                </Select>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* ── Escalate to MCHUSA Modal ── */}
      <Modal
        title="Escalate to MCHUSA Admin"
        open={escalateOpen}
        onOk={() => escalateForm.submit()}
        onCancel={() => { setEscalateOpen(false); escalateForm.resetFields(); setEscalateTarget(null); }}
        okText="Escalate Ticket"
        okButtonProps={{ danger: true }}
        confirmLoading={submitting}
        destroyOnHidden keyboard width={520}>
        {escalateTarget && (
          <div>
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message="This ticket will be escalated to MCHUSA and marked as Escalated. Use this only when you cannot resolve the issue at the organisation level." />
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "#fafafa", borderRadius: 4 }}>
              <Text strong>{escalateTarget.subject}</Text>
              <div style={{ marginTop: 4, fontSize: 13, color: "#595959" }}>{escalateTarget.description}</div>
            </div>
            <Form form={escalateForm} layout="vertical" onFinish={handleEscalateTicket}>
              <Form.Item name="note" label="Escalation Note"
                rules={[{ required: true, message: "Please explain why you are escalating" },
                        { min: 20, message: "At least 20 characters" }]}>
                <Input.TextArea rows={4}
                  placeholder="Explain what you've tried and why this needs MCHUSA attention..." />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* ── New Support Ticket Modal ── */}
      <Modal
        title="New Support Ticket"
        open={ticketCreateOpen}
        onOk={() => ticketForm.submit()}
        onCancel={() => { setTicketCreateOpen(false); ticketForm.resetFields(); }}
        okText="Submit Ticket"
        confirmLoading={submitting}
        destroyOnHidden keyboard width={560}>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}
          initialValues={{ priority: "MEDIUM" }}>
          <Form.Item name="subject" label="Subject"
            rules={[{ required: true, message: "Subject is required" }]}>
            <Input placeholder="Brief description of your issue" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item name="category" label="Category"
                rules={[{ required: true, message: "Please select a category" }]}>
                <Select placeholder="Select category">
                  {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                    <Select.Option key={v} value={v}>{l}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="priority" label="Priority">
                <Select>
                  <Select.Option value="LOW">Low</Select.Option>
                  <Select.Option value="MEDIUM">Normal</Select.Option>
                  <Select.Option value="HIGH">High</Select.Option>
                  <Select.Option value="CRITICAL">Urgent</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description"
            rules={[
              { required: true, message: "Description is required" },
              { min: 20, message: "Please provide at least 20 characters" },
            ]}>
            <Input.TextArea
              rows={5}
              placeholder="Describe your issue. Do not include patient medical information in tickets."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Drawer ── */}
      <Drawer
        title={ticketDetail ? `Ticket — ${String(ticketDetail.id).slice(0,8)}` : "Ticket Detail"}
        open={Boolean(ticketDetail)}
        onClose={() => setTicketDetail(null)}
        width={480}>
        {ticketDetail && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
              <tbody>
                {[
                  ["Subject",   ticketDetail.subject],
                  ["Category",  CATEGORY_LABEL[ticketDetail.category] ?? ticketDetail.category],
                  ["Priority",  <Tag color={PRIORITY_COLOR[ticketDetail.priority] ?? "default"}>{PRIORITY_LABEL[ticketDetail.priority] ?? ticketDetail.priority}</Tag>],
                  ["Status",    <Tag color={TICKET_STATUS_COLOR[ticketDetail.status] ?? "default"}>{ticketDetail.status?.replace(/_/g," ")}</Tag>],
                  ["Submitted", fmtDate(ticketDetail.created_at)],
                ].map(([label, val]) => (
                  <tr key={label} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "8px 12px", color: "#8c8c8c", width: "35%" }}>{label}</td>
                    <td style={{ padding: "8px 12px" }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 6 }}>Description</Text>
              <div style={{ padding: 12, background: "#fafafa", borderRadius: 4, fontSize: 13, color: "#595959" }}>
                {ticketDetail.description}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>MCHUSA Response</Text>
              {ticketDetail.admin_response ? (
                <div style={{ padding: 12, background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4 }}>
                  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 6 }}>
                    Response from MCHUSA
                    {ticketDetail.responded_at && ` — ${fmtDate(ticketDetail.responded_at)}`}
                  </Text>
                  <Text style={{ fontSize: 13 }}>{ticketDetail.admin_response}</Text>
                </div>
              ) : (
                <div style={{ padding: 12, background: "#fafafa", borderRadius: 4,
                  display: "flex", alignItems: "center", gap: 8, color: "#8c8c8c" }}>
                  <ClockCircleOutlined />
                  <Text type="secondary" style={{ fontSize: 13 }}>Awaiting MCHUSA response…</Text>
                </div>
              )}
            </div>

            {ticketDetail.status === "RESOLVED" && (
              <Popconfirm
                title="Close this ticket?"
                description="Closed tickets cannot be reopened."
                onConfirm={() => handleCloseTicket(ticketDetail)}
                okText="Close Ticket" cancelText="Cancel">
                <Button>Close Ticket</Button>
              </Popconfirm>
            )}
          </>
        )}
      </Drawer>

      {/* ── Invoice Detail Modal (Ops Manager) ──────────────────────────── */}
      <Modal
        title={invoiceDetailData ? `Invoice — ${invoiceDetailData.invoice_number}` : "Invoice"}
        open={invoiceDetailOpen}
        onCancel={() => { setInvoiceDetailOpen(false); setInvoiceDetailData(null); }}
        footer={[
          <Button key="close" onClick={() => { setInvoiceDetailOpen(false); setInvoiceDetailData(null); }}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => exportInvoiceCSV(invoiceDetailData)}
            disabled={!invoiceDetailData}>
            Download Invoice
          </Button>,
        ]}
        width={600}
        destroyOnHidden
        keyboard
        confirmLoading={invoiceDetailLoading}>
        {invoiceDetailLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : invoiceDetailData ? (() => {
          const inv = invoiceDetailData;
          const INVOICE_STATUS_BADGE = {
            DRAFT:   { color: "default",  text: "Draft"   },
            SENT:    { color: "blue",     text: "Sent"    },
            VIEWED:  { color: "purple",   text: "Viewed"  },
            PAID:    { color: "success",  text: "Paid"    },
            OVERDUE: { color: "error",    text: "Overdue" },
          };
          const badge = INVOICE_STATUS_BADGE[inv.status] ?? { color: "default", text: inv.status };
          const invMods = inv.subscribed_modules ?? myOrg?.subscribed_modules ?? [];
          const hasMod = (m) => invMods.length === 0 || invMods.includes(m);
          const allLineItems = [
            { service: "Patient Registration",     count: inv.total_registrations,      amount: inv.registration_amount,      show: hasMod("DOCTOR") || hasMod("PATIENT") },
            { service: "Prescription Issued",      count: inv.total_prescriptions,      amount: inv.prescription_amount,      show: hasMod("DOCTOR") },
            { service: "Prescription Dispensed",   count: inv.total_dispenses,          amount: inv.dispense_amount,          show: hasMod("PHARMACY") },
            { service: "Eye Examination",          count: inv.total_eye_exams,          amount: inv.eye_exam_amount,          show: hasMod("OPHTHALMOLOGY") },
            { service: "Lens Prescription Issued", count: inv.total_lens_prescriptions, amount: inv.lens_prescription_amount, show: hasMod("OPHTHALMOLOGY") },
          ];
          const lineItems = allLineItems.filter(li => li.show);
          return (
            <>
              {/* Total Due — prominent */}
              <div style={{
                textAlign: "center", padding: "20px 0 16px",
                borderBottom: "1px solid #f0f0f0", marginBottom: 16,
              }}>
                <Text type="secondary" style={{ fontSize: 13 }}>Total Due</Text>
                <div style={{ fontSize: 32, fontWeight: 700, color: "#1677ff", lineHeight: 1.2 }}>
                  ${parseFloat(inv.total_amount).toFixed(2)}
                </div>
                {inv.due_date && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Due by {new Date(inv.due_date).toLocaleDateString()}
                  </Text>
                )}
              </div>

              {/* Meta */}
              <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Invoice #">
                  <Text code style={{ fontSize: 12 }}>{inv.invoice_number}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Period">{inv.period_label}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={badge.color}>{badge.text}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Sent">
                  {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : "—"}
                </Descriptions.Item>
              </Descriptions>

              {/* Line items */}
              <Table
                size="small"
                pagination={false}
                dataSource={lineItems.map((r, i) => ({ ...r, key: i }))}
                columns={[
                  { title: "Service",  dataIndex: "service", key: "service" },
                  { title: "Count",    dataIndex: "count",   key: "count",  width: 70, align: "center" },
                  { title: "Amount",   dataIndex: "amount",  key: "amount", width: 100, align: "right",
                    render: v => `$${parseFloat(v).toFixed(2)}` },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>${parseFloat(inv.total_amount).toFixed(2)}</Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
                style={{ marginBottom: 16 }}
              />

              {/* Contact */}
              <Alert
                type="info"
                showIcon
                message={
                  <Text style={{ fontSize: 13 }}>
                    To arrange payment, contact{" "}
                    <Text strong>info@mchusa.com</Text>
                    {" "}|{" "}
                    <Text strong>+1 732-322-1902</Text>
                  </Text>
                }
              />
            </>
          );
        })() : null}
      </Modal>
    </Layout>
  );
}
